/**
 * Copy for the deal viability insight.
 *
 * These strings are locked. Do not reword them — the wording was settled over several rounds
 * and the reasoning behind every clause is in `docs/product/MRG-1010-tranches-copy-and-rules.md`.
 * Some of it is not obvious:
 *
 * - Everything points at the customer's floor price, never at an intermediate threshold. Pointing at
 *   90% of floor anchored investors to their lowball: it asked for ~$4,300 when the real gap was
 *   ~$29,800, and it capped the ceiling at 27% when offers at the floor reach 44%.
 * - Odds are percentages in every state. T1's persuasive work is the contrast between its two
 *   figures, and percentages put both on one comparable scale; the natural-frequency form
 *   renders the 44% ceiling as "1 in 3" and shrinks the very gap the sentence exists to show.
 * - Nothing rewards being close. "Almost there" is gone, and an offer just under the floor gets
 *   the same amber treatment as one well under it.
 * - At and above the floor price there is no sentence, only a green tick. Nothing states that
 *   the floor has been cleared, which would hand the investor a bar to come back down to. A
 *   percentage was considered instead, on the grounds that it falls if the offer is lowered,
 *   but the curve is flat from the floor to 1.5x it, so it would hardly move across the range
 *   where it would be shown.
 * - The metric is reaching *a conversation*: the seller engaged. Never "hear back" or "get a
 *   response", which a rejection also satisfies — about 2 in 5 low offers get one of those, so
 *   those phrasings would overstate the figure roughly tenfold.
 * - Never "likely". Two endpoints, never a multiplier.
 * - The shared column talks about *offers*; the unshared column talks about *your chances*.
 *   The unshared column has no denominator it is allowed to name.
 * - T2's two clauses must stay in one element. The copy depends on their adjacency.
 */

import type { OfferViability } from '@/features/offers/api/offers'

const dollars = (amount: number): string => `$${Math.round(amount).toLocaleString('en-US')}`

/**
 * Render the sentence for one insight, or null when it cannot be rendered.
 *
 * Returns null rather than a partial string if the server omitted a figure the chosen
 * variant needs, so a malformed response shows nothing instead of a sentence with a hole.
 */
export function viabilityText(insight: OfferViability, offerPrice: number): string | null {
  const { tier, basis, floor_shared: floorShared, current_pct: current, target_pct: target } =
    insight

  // The top state in both columns is a bare tick with no sentence: at or above the customer's
  // floor price where it is shared, and where there is nothing left to reach where it is not.
  if (tier === 't3') return null

  // Where the floor is not shared the odds are keyed on the House Canary valuation. Red and
  // amber read the same — where the offer stands, and where better offers get to — because
  // without a floor there is no gap to quote and so nothing to distinguish them.
  if (basis === 'avm') {
    if (current == null || target == null) return null
    return (
      `At ${dollars(offerPrice)}, your chances of a conversation are about ${current}%. ` +
      `Stronger offers on this property reach ${target}%.`
    )
  }

  // The last stretch below the floor price. Neither percentage is shown — both round to the
  // same number up here — so the gap is the only figure left. Without a shared floor there is
  // no gap to quote and this tier does not occur; the server sends the tick instead.
  if (tier === 't2' && target == null) {
    const nudge = insight.nudge_amount
    if (nudge == null) return null
    return (
      `Based on the customer's floor price, upping your bid by ${dollars(nudge)} ` +
      `would increase your chances of a conversation.`
    )
  }

  if (current == null || target == null) return null

  if (tier === 't1') {
    if (floorShared) {
      const below = insight.percent_below_floor
      if (below == null) return null
      return (
        `At ${dollars(offerPrice)} you're ${below}% below the customer's floor price. ` +
        `Offers this far below reach a conversation about ${current}% of the time, ` +
        `compared with ${target}% for better offers.`
      )
    }
    return (
      `At ${dollars(offerPrice)}, your chances of a conversation are about ${current}%, ` +
      `compared with ${target}% for better offers.`
    )
  }

  if (floorShared) {
    const nudge = insight.nudge_amount
    if (nudge == null) return null
    return (
      `Based on the customer's floor price, upping your bid by ${dollars(nudge)} ` +
      `increases your chances of a conversation from ${current}% to ${target}%.`
    )
  }
  return (
    `Based on this property's value, a higher bid ` +
    `increases your chances of a conversation from ${current}% to ${target}%.`
  )
}
